/**
 * The supplier portal's goods in (docs/features/supplier-interface/22_warehouse_goods_in_update_spec.md, §4, §6): the
 * goods due into a warehouse the supplier operates for us, and recording what arrived. Beside the warehouse picks
 * (`supplier-picks-adapter.ts`, goods going out), seen only by a signed-in supplier person whose supplier operates a
 * warehouse for us. A separate interface from `SupplierPortalDataAdapter` because it is served on its own:
 * the real implementation (`./api/supplier-portal.ts`, `/supplier-api/goods-in`) exists, the purchase-order half does
 * not yet.
 *
 * Goods in are logins only (no link access), so no call carries an access value: the session cookie is the
 * credential, and a refused credential reads as an `ApiError` 401. The server decides scope (the supplier's
 * warehouses) and what a row allows (`actions`); pages render. A warehouse out of scope is `404`, never "forbidden".
 *
 * **A row is one delivery line** of a bought line: what the warehouse is to receive, how much is in, who is sending
 * it and the sender's despatch (date, carrier, tracking), so the warehouse can match a delivery to it. Never a price,
 * the customer or the job. The id is internal and never shown: the line reference (`ORD-K48193.01`) is what the
 * warehouse quotes to us.
 *
 * One call records one delivery line or many; each entry stands alone, so a bulk call can partly succeed
 * (`GoodsReceivedResult`). The goods-in reference is the idempotency key: the same reference again records nothing
 * more. A request the server refuses whole is an `ApiError`: 400 with field errors in the
 * `.claude/rules/api-validation.md` shape (`receipts.0.goods_in_ref`), read with `extractFieldErrors`.
 */

import type { ListResponse } from "./adapter";
import type {
  GoodsReceivedResult,
  SupplierGoodsInRow,
  SupplierGoodsInTabCounts,
} from "./supplier-api-types";

export type {
  GoodsReceivedOutcome,
  GoodsRefusalReason,
  GoodsReceivedResult,
  SupplierGoodsInRow,
  SupplierGoodsInState,
  SupplierGoodsInTabCounts,
} from "./supplier-api-types";

export type SupplierGoodsInTab = keyof SupplierGoodsInTabCounts;

export const SUPPLIER_GOODS_IN_TABS: readonly SupplierGoodsInTab[] = [
  "due",
  "received_today",
  "all",
];

/** The server's allowed sorts. Left out, it is the earliest expected first (unknown dates last), then the oldest line. */
export type SupplierGoodsInSort =
  "expected_on" | "-expected_on" | "created_at" | "-created_at";

export interface SupplierGoodsInListOptions {
  /** Null when the supplier operates one warehouse (the server defaults it); required when it operates several. */
  warehouseId: string | null;
  tab: SupplierGoodsInTab;
  sort?: SupplierGoodsInSort;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface SupplierGoodsInList extends ListResponse<SupplierGoodsInRow> {
  /** The warehouse listed. */
  warehouseId: string;
  /** Every tab's count under the same search. */
  tabCounts: SupplierGoodsInTabCounts;
  /** Today in London, as the server reads it: the default date of a goods in. */
  today: string;
}

/** Some of one delivery line that arrived. */
export interface GoodsReceivedInput {
  delivery_line_id: string;
  /** How many arrived: at least 1. */
  quantity: number;
  received_on: string;
  /** The warehouse's own reference for the goods in: 1–64 letters, digits, `_` or `-`. The same again records nothing
   *  more. */
  goods_in_ref: string;
}

export interface SupplierGoodsInAdapter {
  /** Refusals are an `ApiError`: 400 `warehouse_id` required (several warehouses, none named), 404 a warehouse not
   *  in scope, 401 not signed in, 422 an unknown sort. */
  listGoodsIn(
    options: SupplierGoodsInListOptions,
  ): Promise<SupplierGoodsInList>;
  /** One result per entry, in the request's order. */
  recordGoodsReceived(
    receipts: GoodsReceivedInput[],
  ): Promise<GoodsReceivedResult[]>;
}
